/**
 * MicroRewards - Positive Feedback System
 * Provides subtle, encouraging feedback after user actions
 * Part of the User Love Layer
 */
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle, Sparkles, Zap, Heart, Star, ThumbsUp } from 'lucide-react';
import { cn } from '@/lib/utils';

// Reward messages by category
const REWARD_MESSAGES = {
  en: {
    category_selected: ["Nice choice!", "Great pick!", "Perfect!"],
    date_selected: ["Date set!", "Scheduled!", "Locked in!"],
    form_progress: ["Looking good!", "Almost there!", "You're doing great!"],
    request_sent: ["Done! We'll handle the rest.", "Request sent!", "You saved time!"],
    provider_matched: ["Matched successfully!", "Found providers!", "Perfect matches found!"],
    response_received: ["New response!", "Good news!", "Someone's interested!"],
    quick_action: ["Fast!", "Speedy!", "Quick work!"],
    booking_confirmed: ["Confirmed!", "All set!", "You're covered!"]
  },
  et: {
    category_selected: ["Hea valik!", "Suurepärane!", "Täiuslik!"],
    date_selected: ["Kuupäev paika!", "Planeeritud!", "Kinnitatud!"],
    form_progress: ["Näeb hea välja!", "Peaaegu valmis!", "Sul läheb hästi!"],
    request_sent: ["Tehtud! Meie hoolitseme ülejäänu eest.", "Päring saadetud!", "Säästsid aega!"],
    provider_matched: ["Sobivus leitud!", "Tegijad leitud!", "Täiuslikud vasted!"],
    response_received: ["Uus vastus!", "Häid uudiseid!", "Keegi on huvitatud!"],
    quick_action: ["Kiire!", "Nobedalt!", "Kiire töö!"],
    booking_confirmed: ["Kinnitatud!", "Kõik korras!", "Oled kaetud!"]
  },
  fi: {
    category_selected: ["Hyvä valinta!", "Loistava!", "Täydellinen!"],
    date_selected: ["Päivä asetettu!", "Ajoitettu!", "Lukittu!"],
    form_progress: ["Näyttää hyvältä!", "Melkein valmis!", "Menet hienosti!"],
    request_sent: ["Valmis! Hoidamme loput.", "Pyyntö lähetetty!", "Säästit aikaa!"],
    provider_matched: ["Sopivat löytyi!", "Tarjoajat löydetty!", "Täydelliset osumat!"],
    response_received: ["Uusi vastaus!", "Hyviä uutisia!", "Joku on kiinnostunut!"],
    quick_action: ["Nopea!", "Ripeästi!", "Nopea työ!"],
    booking_confirmed: ["Vahvistettu!", "Kaikki kunnossa!", "Olet turvassa!"]
  }
};

// Icons for different reward types
const REWARD_ICONS = {
  category_selected: Sparkles,
  date_selected: CheckCircle,
  form_progress: Star,
  request_sent: Heart,
  provider_matched: Zap,
  response_received: ThumbsUp,
  quick_action: Zap,
  booking_confirmed: CheckCircle
};

// Colors for different reward types
const REWARD_COLORS = {
  category_selected: "text-purple-500",
  date_selected: "text-blue-500",
  form_progress: "text-yellow-500",
  request_sent: "text-green-500",
  provider_matched: "text-orange-500",
  response_received: "text-pink-500",
  quick_action: "text-cyan-500",
  booking_confirmed: "text-emerald-500"
};

/**
 * Get a random reward message for a given action type
 */
export const getRewardMessage = (type, language = 'en') => {
  const messages = REWARD_MESSAGES[language] || REWARD_MESSAGES.en;
  const typeMessages = messages[type] || messages.form_progress;
  return typeMessages[Math.floor(Math.random() * typeMessages.length)];
};

/**
 * MicroReward Toast - Shows a brief, encouraging message
 */
export const MicroRewardToast = ({ 
  type = 'form_progress', 
  message, 
  language = 'en',
  duration = 2000,
  onComplete 
}) => {
  const [visible, setVisible] = useState(true);
  const displayMessage = message || getRewardMessage(type, language);
  const Icon = REWARD_ICONS[type] || Sparkles;
  const colorClass = REWARD_COLORS[type] || "text-green-500";

  useEffect(() => {
    const timer = setTimeout(() => {
      setVisible(false);
      onComplete?.();
    }, duration);
    return () => clearTimeout(timer);
  }, [duration, onComplete]);

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0, y: -10, scale: 0.9 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -10, scale: 0.9 }}
          className="inline-flex items-center gap-2 px-3 py-1.5 bg-white rounded-full shadow-lg border"
        >
          <Icon className={cn("h-4 w-4", colorClass)} />
          <span className="text-sm font-medium text-slate-700">{displayMessage}</span>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

/**
 * MicroReward Inline - Shows inline feedback within forms
 */
export const MicroRewardInline = ({ 
  type = 'form_progress', 
  message, 
  language = 'en',
  show = true,
  className 
}) => {
  const displayMessage = message || getRewardMessage(type, language);
  const Icon = REWARD_ICONS[type] || Sparkles;
  const colorClass = REWARD_COLORS[type] || "text-green-500";

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ opacity: 0, x: -10 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -10 }}
          className={cn(
            "inline-flex items-center gap-1.5 text-sm",
            className
          )}
        >
          <Icon className={cn("h-3.5 w-3.5", colorClass)} />
          <span className={cn("font-medium", colorClass)}>{displayMessage}</span>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

/**
 * Success Celebration - For major completions
 */
export const SuccessCelebration = ({ 
  title, 
  subtitle, 
  language = 'en' 
}) => {
  const defaultTitle = {
    en: "You're done! We'll handle the rest.",
    et: "Valmis! Meie hoolitseme ülejäänu eest.",
    fi: "Valmis! Hoidamme loput."
  };

  const defaultSubtitle = {
    en: "Relax. Providers are being notified.",
    et: "Võta rahulikult. Tegijaid teavitatakse.",
    fi: "Rentoudu. Tarjoajia ilmoitetaan."
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="text-center py-8"
    >
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
        className="w-16 h-16 mx-auto bg-gradient-to-br from-green-400 to-emerald-500 rounded-full flex items-center justify-center mb-4"
      >
        <CheckCircle className="h-8 w-8 text-white" />
      </motion.div>
      <motion.h3
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="text-xl font-bold text-slate-900 mb-2"
      >
        {title || defaultTitle[language] || defaultTitle.en}
      </motion.h3>
      <motion.p
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="text-slate-600"
      >
        {subtitle || defaultSubtitle[language] || defaultSubtitle.en}
      </motion.p>
    </motion.div>
  );
};

/**
 * Progress Indicator with Encouragement
 */
export const ProgressWithLove = ({ 
  steps, 
  currentStep, 
  language = 'en' 
}) => {
  const progress = (currentStep / steps) * 100;
  
  const encouragements = {
    en: ["Let's go!", "Good start!", "Halfway there!", "Almost done!", "One more step!"],
    et: ["Alustame!", "Hea algus!", "Pool teed!", "Peaaegu valmis!", "Veel üks samm!"],
    fi: ["Aloitetaan!", "Hyvä alku!", "Puoliväli!", "Melkein valmis!", "Vielä yksi!"]
  };

  const messages = encouragements[language] || encouragements.en;
  const messageIndex = Math.min(Math.floor((currentStep / steps) * messages.length), messages.length - 1);

  return (
    <div className="space-y-2">
      <div className="flex justify-between items-center text-sm">
        <span className="text-slate-500">{currentStep} / {steps}</span>
        <motion.span
          key={messageIndex}
          initial={{ opacity: 0, x: 10 }}
          animate={{ opacity: 1, x: 0 }}
          className="text-orange-500 font-medium"
        >
          {messages[messageIndex]}
        </motion.span>
      </div>
      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
        <motion.div
          className="h-full bg-gradient-to-r from-orange-400 to-yellow-400"
          initial={{ width: 0 }}
          animate={{ width: `${progress}%` }}
          transition={{ duration: 0.5, ease: "easeOut" }}
        />
      </div>
    </div>
  );
};

export default {
  MicroRewardToast,
  MicroRewardInline,
  SuccessCelebration,
  ProgressWithLove,
  getRewardMessage
};
